import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, SearchIcon, CheckIcon, XIcon } from 'lucide-react';
interface SearchableSelectProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  name?: string;
}
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className = '',
  name
}: SearchableSelectProps) {
  const {
    t
  } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const filtered = search ? options.filter(opt => opt.toLowerCase().includes(search.toLowerCase())) : options;
  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('li');
      items[highlightedIndex]?.scrollIntoView({
        block: 'nearest'
      });
    }
  }, [highlightedIndex]);
  const handleSelect = (opt: string) => {
    onChange(opt);
    setIsOpen(false);
    setSearch('');
    setHighlightedIndex(-1);
  };
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
    setHighlightedIndex(-1);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
        return;
      }
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => prev < filtered.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : filtered.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
          handleSelect(filtered[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearch('');
        setHighlightedIndex(-1);
        break;
    }
  };
  const handleOpen = () => {
    setIsOpen(true);
    setHighlightedIndex(-1);
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  return <div ref={containerRef} className={`relative ${className}`} data-id="element-15501">
      {/* Hidden input for form compatibility */}
      {name && <input type="hidden" name={name} value={value} data-id="element-15502" />}

      {/* Trigger button */}
      <button type="button" aria-expanded={isOpen} aria-haspopup="listbox" onClick={handleOpen} onKeyDown={handleKeyDown} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-left flex items-center justify-between transition-colors focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--brand-primary)] focus:outline-none ${isOpen ? 'ring-2 ring-[var(--brand-primaryring)] border-[var(--brand-primary)]' : 'hover:border-gray-300'} ${value ? 'text-gray-900' : 'text-gray-400'} bg-white`} data-id="element-15503">
        <span className="truncate" data-id="element-15504">{value || placeholder}</span>
        <div className="flex items-center gap-1 ml-2 flex-shrink-0" data-id="element-15505">
          {value && <span role="button" tabIndex={-1} onClick={handleClear} aria-label="Clear selection" className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-15506">
              <XIcon size={14} aria-hidden="true" data-id="element-15507" />
            </span>}
          <ChevronDownIcon size={16} className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-15508" />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150" data-id="element-15509">
          {/* Search input */}
          <div className="p-2 border-b border-gray-100" data-id="element-15510">
            <div className="relative" data-id="element-15511">
              <SearchIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-15512" />

              <input ref={inputRef} type="text" value={search} onChange={e => {
            setSearch(e.target.value);
            setHighlightedIndex(0);
          }} onKeyDown={handleKeyDown} placeholder={t('ui.SearchableSelect.attrs.type_to_search')} aria-label={t('ui.SearchableSelect.attrs.type_to_search')} className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-[var(--brand-primaryring)] focus:border-[var(--brand-primary)] focus:outline-none" autoComplete="off" data-id="element-15513" />
            </div>
          </div>

          {/* Options list */}
          <ul ref={listRef} className="max-h-52 overflow-auto py-1" role="listbox" data-id="element-15514">
            {filtered.length === 0 ? <li className="px-4 py-3 text-sm text-gray-400 text-center" data-id="element-15515">
                {t('ui.SearchableSelect.no_results_found')}
              </li> : filtered.map((opt, i) => <li key={opt} role="option" aria-selected={opt === value} onClick={() => handleSelect(opt)} onMouseEnter={() => setHighlightedIndex(i)} className={`px-4 py-2 text-sm cursor-pointer flex items-center justify-between transition-colors ${i === highlightedIndex ? 'bg-[var(--brand-primarysubtle)] text-gray-900' : opt === value ? 'bg-[var(--brand-primarysubtle)] text-gray-900' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-15516">
                  <span data-id="element-15517">{opt}</span>
                  {opt === value && <CheckIcon size={14} className="text-[#B5630A] flex-shrink-0" data-id="element-15518" />}
                </li>)}
          </ul>
        </div>}
    </div>;
}